//dependencies
import React, { ReactNode, useEffect } from "react";

import { faCommentDots } from "@fortawesome/pro-duotone-svg-icons";
import { faPlus, faXmark } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { toast } from "@performyard/py-components";
//components
import { Avatar } from "@performyard/py-components";
import { sanitizeHtmlContent, validateContentSecurity } from "@performyard/utils-isomorphic";

import Button from "../../../../components/Button";
import IconButton from "../../../../components/Button/IconButton";
import EmployeePicker, { SingleEmployee } from "../../../../components/EmployeePicker";
import { RadioButtons } from "../../../../components/Inputs/SelectionOptions/RadioButtons";
import Panel from "../../../../components/Panel";
import ComponentLoader from "../../../../components/Spinner/ComponentLoader";
import { TextEditor } from "../../../../components/TextEditor";

//api
import { useCompanyFunction, useCurrentUser, useUser } from "../../../../api/hooks";
import { useCompanyHashtags, useMutateSingleFeedback, useSingleFeedback } from "../../../../api/hooks/defs/feedback";
import useForm from "../../../../hooks/useForm";

//utils
import { FeedbackAttributes, FeedbackSchema, calculateShareOptions, getSubmitButtonLabel } from "./utils";

//store
import { useFeedbackDrawerStore } from "../../FeedbackDrawerStore";
//css needed for employee chip styling, this is holdover from some older setup
import "./FeedbackDrawer.css";

const FeedbackDrawer = () => {
	const { closeDrawer, requestData, feedbackId, isOpen } = useFeedbackDrawerStore();
	const { data, isLoading } = useSingleFeedback(feedbackId);
	const { mutate: createFeedback, isPending } = useMutateSingleFeedback({
		onSuccess: closeDrawer,
	});
	const { data: meResponse } = useCurrentUser();
	const user = meResponse?.me?.authorization?.payload?.user_id;
	const { data: currentUser } = useUser({ userID: user });
	const {
		attributes = {
			notes: false,
			notesManager: false,
			allowRecognition: false,
			allowOneOne: false,
			allowSubjectManager: false,
		},
	} = useCompanyFunction("feedback");
	const { data: hashtags, isLoading: isHashtagsLoading } = useCompanyHashtags();

	const { state, setNewDefaultValues, updateForm } = useForm({
		schema: FeedbackSchema,
		defaultValues: {
			subject_ids: [],
			subjects_list: [],
			shared_with: "",
		},
		disableWarning: !isOpen,
	});

	useEffect(() => {
		if (isOpen) {
			if (requestData?.requestedBy) {
				updateForm({
					subjects_list: [{ _id: requestData.requestedBy._id, name: requestData.requestedBy.name }],
				});
			}
		} else {
			setNewDefaultValues({
				subjects_list: [],
				shared_with: "",
				content: "",
			});
			updateForm({
				subjects_list: [],
				shared_with: "",
				content: "",
			});
		}
	}, [isOpen]);

	useEffect(() => {
		if (!isLoading && data && isOpen && JSON.stringify(data) !== JSON.stringify(state)) {
			updateForm({
				subjects_list: data.subjects_list,
				shared_with: data.shared_with,
				content: data.content,
			});
		}
	}, [isLoading, data?._id]);

	const isCurrentUserInStack = state?.subjects_list?.some((v) => v._id === user);
	const options = calculateShareOptions({
		selectedUsers: state.subjects_list.map((subject) => subject._id),
		currentUser: user || "",
		value: state.shared_with,
		//set all attributes to true if their value is 'enabled'
		attributes: Object.keys(attributes).reduce(
			(acc, key) => ({ ...acc, [key as FeedbackAttributes]: attributes[key] || attributes[key] === "enabled" }),
			{}
		) as { [key in FeedbackAttributes]: boolean },
	});

	useEffect(() => {
		// this handles the case where the user changes the employees and exposes or removes an option
		if (isCurrentUserInStack && state.subjects_list.length === 1 && state.shared_with !== "me") {
			updateForm({ shared_with: "" });
		}
	}, [state.subjects_list.length]);

	const handleSubmit = () => {
		const sanitizedContent = sanitizeHtmlContent(state.content);

		const validation = validateContentSecurity(sanitizedContent);
		if (!validation.isValid || !sanitizedContent?.trim()) {
			toast.error("Invalid feedback content");
			return;
		}

		createFeedback({
			id: feedbackId,
			aboutList: state.subjects_list.map((v) => v._id),
			content: sanitizedContent,
			feedback_message: sanitizedContent,
			shareWith: state.shared_with,
			feedbackRequest: requestData?.requestId,
		});
	};

	let shareInput: ReactNode | null = null;
	if (state?.subjects_list?.length === 1 && isCurrentUserInStack && !attributes["notes"]) {
		shareInput = (
			<div className="text-paragraph-3 text-red-6">
				Permissions do not allow you to give feedback solely about yourself.
			</div>
		);
	} else if (data?.shared_with && !options.some((option) => option.value === data.shared_with)) {
		shareInput = (
			<div className="text-paragraph-3 text-red-6">
				Permission changes require you to select a new audience to edit this content
			</div>
		);
	} else if (options.length > 0) {
		shareInput = (
			<RadioButtons
				options={options}
				value={{ label: "value", value: state.shared_with }}
				onChange={(value) => updateForm({ shared_with: value.value })}
			/>
		);
	}

	return (
		<Panel side="right" isOpen={isOpen} onClose={closeDrawer} width={480}>
			{isLoading || isHashtagsLoading ? (
				<ComponentLoader />
			) : (
				<div className="w-[90vw] max-w-[416px] p-6" data-testid="leave-feedback-drawer-contents">
					<h2
						data-testid="leave-feedback-title"
						className="margin-auto max-w-full text-center text-[30px] font-[900] text-primary-5"
					>
						<FontAwesomeIcon icon={faCommentDots} /> GIVE FEEDBACK
					</h2>
					<div id="about container" className="mt-4 flex max-w-full gap-2">
						<div style={{ zIndex: 2 }} className={isCurrentUserInStack ? "w-full" : "w-[calc(100%-120px)]"}>
							<EmployeePicker
								label="Who is this feedback about?"
								placeholder="Select an employee..."
								onChange={(value) =>
									updateForm({
										subjects_list:
											value && Array.isArray(value)
												? (value as SingleEmployee[])?.map((v) => ({
														_id: v.value,
														name: v.label,
													}))
												: [],
									})
								}
								value={state.subjects_list?.map((v) => ({ value: v._id, label: v.name }))}
								isMultiSelect
							/>
						</div>
						{!isCurrentUserInStack && (
							<div className="flex-shrink-none mt-[24px] w-[104px] text-nowrap">
								<Button
									icon={faPlus}
									onClick={() => {
										updateForm({
											subjects_list: [
												...state.subjects_list,
												{ _id: user || "", name: currentUser?.name || "" },
											],
										});
									}}
								>
									Add Myself
								</Button>
							</div>
						)}
					</div>
					<div id="share container" className="mt-4">
						<p className="pb-1 text-paragraph-3 font-semi-bold">Who will you share this feedback with?</p>
						{shareInput}
					</div>
					{requestData?.requestedBy && (
						<div id="message" className="mt-4">
							<div className="flex flex-wrap text-wrap text-paragraph-3 font-semibold">
								<span>Message from </span>
								<Avatar
									size={16}
									id={requestData?.requestedBy._id}
									name={requestData?.requestedBy.name || "User"}
									dangerouslySetClassName="mx-1"
								/>
								<span>{requestData?.requestedBy.name}</span>
							</div>
							<p className="bg-neutral-2 text-paragraph-3">{requestData?.message}</p>
						</div>
					)}
					<div id="feedback container" className="mb-20 mt-4">
						<div className="mb-1 flex justify-between">
							<div className="text-paragraph-3 font-semibold">Feedback</div>
							{hashtags && hashtags?.length > 0 ? (
								<div className="text-paragraph-3 text-neutral-4">
									{"Type '#' to view any company hashtags"}
								</div>
							) : null}
						</div>
						<TextEditor
							key={feedbackId || "slideout"}
							onChange={(value) => updateForm({ content: value })}
							initialValue={data?.content}
							placeholder="Type your feedback here..."
							hashTags={hashtags && hashtags.length > 0 ? hashtags : undefined}
							namespace={isLoading ? feedbackId : "slideout"}
							className={"max-w-full"}
							minHeight={200}
							autoFocus
						/>
					</div>
				</div>
			)}
			{state.shared_with && state?.subjects_list?.length && state.content ? (
				<div className="fixed bottom-0 w-[465px] max-w-full bg-neutral-0 p-3">
					<Button
						isRequesting={isPending}
						testid="leave-feedback-submit-button"
						size="large"
						style={{ width: "calc(100% - 24px)", margin: 8 }}
						onClick={handleSubmit}
					>
						{getSubmitButtonLabel(
							state.shared_with,
							isCurrentUserInStack && state.subjects_list.length == 1
						)}
					</Button>
				</div>
			) : null}

			<IconButton
				testId="leave-feedback-close-button"
				icon={faXmark}
				className="absolute right-2 top-2"
				onClick={closeDrawer}
				aria-label="Close feedback drawer"
			/>
		</Panel>
	);
};

export default FeedbackDrawer;
